import React, { useRef, useState, forwardRef, useImperativeHandle } from "react";
import { GenerationSection } from "./GenerateDraftSidebar";
import TreatmentPlanSection from "./TreatmentPlanSection";
import { exportTreatmentPlan } from "../services/api";
import "./ReviewStep.css";

interface ReviewStepProps {
  generatedSections: Map<GenerationSection, any>;
  onSectionDataChange?: (section: GenerationSection, newData: any) => void;
}

export interface ReviewStepHandle {
  scrollToSection: (section: GenerationSection) => void;
}

const SECTION_ORDER: GenerationSection[] = [
  GenerationSection.DOCUMENT_HEADER,
  GenerationSection.PATIENT_INFORMATION,
  GenerationSection.DIAGNOSTIC_INFORMATION,
  GenerationSection.HISTORY_OF_SERVICES,
  GenerationSection.RELEVANT_HISTORY,
  GenerationSection.DIRECT_AND_INDIRECT_MEASURES,
  GenerationSection.INITIAL_OBSERVATION,
  GenerationSection.LANGUAGE_DOMAIN,
  GenerationSection.SOCIAL_AND_PLAY_DOMAIN,
  GenerationSection.ATTENDING_SKILLS_DOMAIN,
  GenerationSection.DAILY_LIVING_DOMAIN,
  GenerationSection.SAFETY_SKILLS_DOMAIN,
  GenerationSection.FUNCTIONAL_BEHAVIOR_ASSESSMENT,
  GenerationSection.CLINICAL_RECOMMENDATIONS,
  GenerationSection.LANGUAGE_GOALS,
  GenerationSection.SOCIAL_AND_PLAY_GOALS,
  GenerationSection.ATTENDING_GOALS,
  GenerationSection.DAILY_LIVING_GOALS,
  GenerationSection.SAFETY_GOALS,
  GenerationSection.CAREGIVER_GOALS,
  GenerationSection.PLAN_FOR_GENERALIZATION,
  GenerationSection.TRANSITION_PLANNING,
  GenerationSection.CRITERIA_FOR_DISCHARGE,
  GenerationSection.COORDINATION_OF_CARE,
  GenerationSection.CRISIS_MANAGEMENT_PLANNING,
  GenerationSection.REQUESTED_HOURS_OF_SERVICE,
];

const ReviewStep = forwardRef<ReviewStepHandle, ReviewStepProps>(({
  generatedSections,
  onSectionDataChange,
}, ref) => {
  const sectionRefs = useRef<Map<GenerationSection, HTMLDivElement>>(new Map());
  const [isExportingWord, setIsExportingWord] = useState(false);
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const availableSections = SECTION_ORDER.filter((section) =>
    generatedSections.has(section) && generatedSections.get(section) !== null
  );

  const scrollToSection = (section: GenerationSection) => {
    const element = sectionRefs.current.get(section);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  useImperativeHandle(ref, () => ({ scrollToSection }));

  const handleExport = async (format: "word" | "pdf") => {
    const setExporting = format === "word" ? setIsExportingWord : setIsExportingPdf;
    setExporting(true);
    setExportError(null);

    try {
      const blob = await exportTreatmentPlan(generatedSections, format);

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;

      const timestamp = new Date().toISOString().slice(0, 10);
      const extension = format === "word" ? "docx" : "pdf";
      a.download = `treatment-plan-${timestamp}.${extension}`;

      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Export error:", error);
      setExportError(
        error instanceof Error ? error.message : `Failed to export treatment plan as ${format.toUpperCase()}`
      );
    } finally {
      setExporting(false);
    }
  };

  const isExporting = isExportingWord || isExportingPdf;

  return (
    <div className="review-step">
      <div className="review-step__header">
        <h2>Treatment Plan Review</h2>
        <div className="review-header-actions">
          <button
            className="btn btn-secondary"
            onClick={() => handleExport("word")}
            disabled={isExporting || availableSections.length === 0}
            title={availableSections.length === 0 ? "Generate sections first" : "Export to Word"}
          >
            {isExportingWord ? (
              <>
                <span className="export-spinner"></span>
                Exporting...
              </>
            ) : (
              "Export to Word"
            )}
          </button>
          <button
            className="btn btn-primary"
            onClick={() => handleExport("pdf")}
            disabled={isExporting || availableSections.length === 0}
            title={availableSections.length === 0 ? "Generate sections first" : "Export to PDF"}
          >
            {isExportingPdf ? (
              <>
                <span className="export-spinner"></span>
                Exporting...
              </>
            ) : (
              "Export to PDF"
            )}
          </button>
        </div>
      </div>

      {exportError && (
        <div className="export-error-banner">
          <span className="error-icon">⚠️</span>
          <span>{exportError}</span>
          <button onClick={() => setExportError(null)}>×</button>
        </div>
      )}

      <div className="review-step__body">
        <div className="review-step__content">
          {availableSections.length === 0 ? (
            <div className="no-sections-message">
              <span className="empty-icon">📝</span>
              <h3>No Sections Generated</h3>
              <p>Generate sections in the previous steps to see the full treatment plan preview here.</p>
            </div>
          ) : (
            <div className="treatment-plan-document">
              {availableSections.map((section, index) => (
                <div
                  key={section}
                  ref={(el) => {
                    if (el) sectionRefs.current.set(section, el);
                  }}
                  className="plan-section-wrapper"
                >
                  <TreatmentPlanSection
                    section={section}
                    data={generatedSections.get(section)}
                    editable={true}
                    onDataChange={(newData) => {
                      if (onSectionDataChange) {
                        onSectionDataChange(section, newData);
                      }
                    }}
                  />
                  {index < availableSections.length - 1 && (
                    <div className="section-divider" />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

export default ReviewStep;
